import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { SelectedStudent } from './selected-student';
export { SelectedStudent };
import { selectedStudentSchema } from '@schemas/business-objects/selected-student';
import { NotAvailableFormArray } from './not-available-form-group';
import { StudentFormGroup } from './student-form-group';
import { StudentAssignmentFormArray } from './student-assignment-form-group';
import { TermFormGroup } from './term-form-group';
import type { CalendarDate } from '../base-types';
import type { Organization } from './organization';
import type { Slot } from './slot';
import type { TermTime } from './term-time';
import type { TermTutor } from './term-tutor';
import type { Tutor } from './tutor';
import type { User } from './user';

export class SelectedStudentFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    created: SchemaFormControl;
    inactive: SchemaFormControl;
    inactiveEffectiveDate: SchemaFormControl;
    statusNotes: SchemaFormControl;
    termId: SchemaFormControl;
    term: TermFormGroup;
    studentId: SchemaFormControl;
    student: StudentFormGroup;
    studentAssignments: StudentAssignmentFormArray;
    notAvailables: NotAvailableFormArray;
  };

  declare value: Partial<SelectedStudent>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(selectedStudentSchema, SelectedStudent, data, options);
  }

  get object(): SelectedStudent {
    return new SelectedStudent(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'term':
        return new TermFormGroup();
      case 'student':
        return new StudentFormGroup();
      case 'studentAssignments':
        return new StudentAssignmentFormArray();
      case 'notAvailables':
        return new NotAvailableFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<SelectedStudent[]> {
    return SelectedStudent.find(filter);
  }

  static async findOne(filter?: any): Promise<SelectedStudent | null> {
    return SelectedStudent.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<SelectedStudent> {
    return SelectedStudent.findById(id, filter);
  }

  static async create(
    data: Partial<SelectedStudent>,
  ): Promise<SelectedStudent> {
    return SelectedStudent.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<SelectedStudent>,
  ): Promise<SelectedStudent> {
    return SelectedStudent.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return SelectedStudent.deleteById(id);
  }

  async markInactive(
    effectiveDate?: CalendarDate | null,
  ): Promise<SelectedStudentFormGroup> {
    const instance = new SelectedStudent(this.getRawValue());
    const result = await instance.markInactive(effectiveDate);
    const fg = new SelectedStudentFormGroup();
    fg._populate(result);
    return fg;
  }
}

export class SelectedStudentFormArray extends PersistedFormArray {
  readonly entityName = 'SelectedStudent' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(selectedStudentSchema, SelectedStudent, options);
  }

  protected override createItemGroup() {
    return new SelectedStudentFormGroup();
  }

  get array(): SelectedStudent[] {
    return this.controls.map((group: SelectedStudentFormGroup) => group.object);
  }
}

export class SelectedStudentPersistedArray extends PersistedArray<SelectedStudent> {
  readonly entityName = 'SelectedStudent' as const;

  constructor(options?: PersistedArrayOptions) {
    super(SelectedStudent, options);
  }
}
